import {
  ActivitiesRow,
  EnrollmentsRow,
  PricingOffersRow,
  SectionsRow,
  UsersRow
} from "@outschool/db-queries";
import {
  hasOngoingEnded as _hasOngoingEnded,
  isPaused
} from "@outschool/enrollment";
import {
  BulkEnrollmentInput,
  ChildInput
} from "@outschool/gql-frontend-generated";
import { joinCommasAnd } from "@outschool/text";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import _ from "lodash";

import * as Activity from "./Activity";
import { ACTIVITY_FORMAT } from "./Activity";
import * as EnrollmentPaymentRefund from "./EnrollmentPaymentRefund";
import { REVIEWS_TIME_LIMIT_IN_DAYS } from "./Feedback";
import {
  EnrollmentAdminRow_EnrollmentFragment,
  EnrollmentAdminRow_GiftEnrollmentFragment,
  EnrollmentAdminRow_PaymentFragment,
  EnrollmentInput
} from "./generated/graphql";
import * as helpers from "./helpers";
import * as Section from "./Section";

export const isAttended = (enrollment: $TSFixMe) =>
  !!enrollment.details?.attendedAt;

const isAttendedMeetingAndInReviewTimeWindow = (enrollment: EnrollmentsRow) =>
  !!enrollment.details?.attendedAt &&
  dayjs(enrollment.details?.attendedAt)
    .add(REVIEWS_TIME_LIMIT_IN_DAYS, "days")
    .isAfter();

type IsConfirmedArgs = {
  confirmed_at?: Date | null;
  confirmedAt?: string;
};
export const isConfirmed = ({ confirmed_at, confirmedAt }: IsConfirmedArgs) =>
  Boolean(confirmed_at || confirmedAt);

export const isOngoing = (
  enrollment: Pick<EnrollmentsRow, "ongoing_start_time">
) => Boolean(enrollment.ongoing_start_time);

export const isValid = (enrollment: IsConfirmedArgs & IsWithdrawnArgs) =>
  !!(isConfirmed(enrollment) && !isWithdrawn(enrollment));

export const isFree = (enrollment: $TSFixMe) => !enrollment.price_cents;

type IsWithdrawnArgs = {
  withdrawn_at?: Date | null;
  withdrawnAt?: string;
};
export const isWithdrawn = ({ withdrawn_at, withdrawnAt }: IsWithdrawnArgs) =>
  Boolean(withdrawn_at || withdrawnAt);

type IsUnclaimedGiftArgs = {
  purchased_as_gift?: boolean;
  purchasedAsGift?: boolean;
  gift_claimed_at?: Date;
  giftClaimedAt?: string;
};
export const isUnclaimedGift = ({
  purchased_as_gift,
  purchasedAsGift,
  gift_claimed_at,
  giftClaimedAt
}: IsUnclaimedGiftArgs) =>
  Boolean(purchased_as_gift || purchasedAsGift) &&
  !Boolean(gift_claimed_at || giftClaimedAt);

export const isPurchasedAsGift = ({
  purchased_as_gift,
  purchasedAsGift
}: $TSFixMe) => Boolean(purchased_as_gift || purchasedAsGift);

type HasOnGoingStartedArgs = {
  ongoing_start_time?: Date | string | null;
  ongoingStartTime?: Date | string | null;
};
export const hasOngoingStarted = ({
  ongoing_start_time,
  ongoingStartTime
}: HasOnGoingStartedArgs) => {
  const _ongoingStartTime = ongoing_start_time || ongoingStartTime;
  if (!!_ongoingStartTime) {
    return Time.dayjs(_ongoingStartTime).isBefore(Time.dayjs());
  }
  return false;
};

type IsOngoingStoppedArgs = {
  ongoingEndTime?: Date;
  ongoing_end_time?: Date;
  pause_window_uid?: number;
  pauseWindow?: object | null;
};
export const isOngoingStopped = ({
  ongoingEndTime,
  ongoing_end_time,
  pause_window_uid,
  pauseWindow
}: IsOngoingStoppedArgs) =>
  Boolean(ongoing_end_time || ongoingEndTime) &&
  !Boolean(pause_window_uid || pauseWindow);

type IsOngoingStoppedAtArgs = {
  ongoingStoppedAt?: Date;
  ongoing_stopped_at?: Date;
};
export const isOngoingStoppedAt = ({
  ongoingStoppedAt,
  ongoing_stopped_at
}: IsOngoingStoppedAtArgs) => Boolean(ongoingStoppedAt || ongoing_stopped_at);

export const hasOngoingEnded = ({
  ongoing_end_time,
  pause_window_uid
}: {
  pause_window_uid: string | null;
  ongoing_end_time: Date | null;
}) => _hasOngoingEnded({ ongoing_end_time, pause_window_uid });

type WasOngoingActiveAtArgs = {
  ongoing_start_time?: string | Date;
  ongoingStartTime?: string | Date;
  ongoingEndTime?: string | Date;
  ongoing_end_time?: string | Date;
};
export const wasOngoingActiveAt = (
  {
    ongoing_start_time,
    ongoingStartTime,
    ongoingEndTime,
    ongoing_end_time
  }: WasOngoingActiveAtArgs,
  start: string | Date,
  end?: string | Date
) => {
  const startTime = Time.dayjs(start);
  const endTime = Time.dayjs(end || start);
  return (
    startTime.isSameOrAfter(
      Time.dayjs(ongoing_start_time || ongoingStartTime)
    ) &&
    (!(ongoingEndTime || ongoing_end_time) ||
      endTime.isSameOrBefore(Time.dayjs(ongoingEndTime || ongoing_end_time)))
  );
};
export const wasOneOnOneActiveAt = (
  {
    ongoing_start_time,
    ongoingStartTime,
    ongoingEndTime,
    ongoing_end_time
  }: WasOngoingActiveAtArgs,
  start: string | Date,
  end?: string | Date
) => {
  const startTime = Time.dayjs(start);
  const endTime = Time.dayjs(end || start);
  return (
    !(ongoingEndTime || ongoing_end_time) ||
    (startTime.isSameOrAfter(
      Time.dayjs(ongoing_start_time || ongoingStartTime)
    ) &&
      endTime.isSameOrBefore(Time.dayjs(ongoingEndTime || ongoing_end_time)))
  );
};

export const filterFunctions = {
  Confirmed: (e: EnrollmentsRow) => isConfirmed(e),
  Attended: (e: EnrollmentsRow) => isValid(e) && isAttended(e),
  Absent: (e: EnrollmentsRow) => isValid(e) && !isAttended(e),
  Withdrawn: (e: EnrollmentsRow) => isWithdrawn(e),
  // TODO(HP-2277): make Enrolled, Active, and Ended properly handle non-ongoing section completion
  Enrolled: (e: EnrollmentsRow) => isValid(e) && !hasOngoingEnded(e),
  Active: (e: EnrollmentsRow) =>
    isValid(e) &&
    (!isOngoing(e) ||
      (isOngoing(e) && !hasOngoingEnded(e) && hasOngoingStarted(e))),
  Ended: (e: EnrollmentsRow) => isValid(e) && hasOngoingEnded(e),
  Paused: (e: EnrollmentsRow) => isPaused(e) && !isWithdrawn(e)
};

export function canReview(
  enrollment: $TSFixMe,
  section: $TSFixMe,
  activity: $TSFixMe
) {
  const isValidEnrollment = isValid(enrollment);

  const isAttendedSection =
    isAttended(enrollment) || Activity.isFlexSchedule(activity) ? true : false;

  const isWithdrawnOrStoppedAfterStarted =
    isConfirmed(enrollment) &&
    (isWithdrawn(enrollment) || isOngoingStoppedAt(enrollment)) &&
    Time.dayjs(enrollment.withdrawn_at).isAfter(section.start_time) &&
    (!isOngoing(enrollment) ||
      Time.dayjs(
        enrollment.withdrawn_at || enrollment.ongoing_stopped_at
      ).isAfter(enrollment.ongoing_start_time));

  let isInReviewTimeWindow = false;
  if (isOngoing(enrollment)) {
    isInReviewTimeWindow =
      hasOngoingStarted(enrollment) &&
      (!(hasOngoingEnded(enrollment) || enrollment.isEnded) ||
        Time.dayjs(
          _.max([enrollment.ongoing_stopped_at, enrollment.ongoing_end_time])
        )
          .add(REVIEWS_TIME_LIMIT_IN_DAYS, "day")
          .isAfter(Time.dayjs()));
  } else {
    // one on one format would never have section end_time,
    // check whether they attened at least one meeting instead
    if (Activity.format(activity) === ACTIVITY_FORMAT.ONE_ON_ONE) {
      isInReviewTimeWindow = isAttendedMeetingAndInReviewTimeWindow(enrollment);
    } else {
      isInReviewTimeWindow =
        Section.isPast(section) && Section.isInReviewTimeWindow(section);
    }
  }
  return (
    (isValidEnrollment && isInReviewTimeWindow && isAttendedSection) ||
    isWithdrawnOrStoppedAfterStarted
  );
}

export const publicView = (e: $TSFixMe) => {
  return _.pick(e, [
    "uid",
    "section_uid",
    "user_uid",
    "confirmed_at",
    "withdrawn_at"
  ]);
};

export const leaderView = (e: $TSFixMe, hasBackgroundChecked: $TSFixMe) => {
  if (hasBackgroundChecked) {
    return e;
  } else {
    return _.pick(e, [
      "uid",
      "section_uid",
      "user_uid",
      "confirmed_at",
      "withdrawn_at"
    ]);
  }
};

export const userRole = (
  enrollment: EnrollmentsRow,
  activity: ActivitiesRow,
  user: Pick<UsersRow, "uid" | "is_admin">
) => {
  if (Activity.canManage(activity, user)) {
    return user.uid === activity.user_uid
      ? EnrollmentPaymentRefund.USER_ROLES.TEACHER
      : EnrollmentPaymentRefund.USER_ROLES.OUTSCHOOL;
  } else if (user.uid === enrollment.user_uid) {
    return EnrollmentPaymentRefund.USER_ROLES.PARENT;
  }
  return undefined;
};

export function groupIntoListings<S extends SectionsRow>(
  enrollments: EnrollmentsRow[],
  activities: ActivitiesRow[],
  sections: S[],
  leaders: UsersRow[]
): {
  enrollments: EnrollmentsRow[];
  section: S;
  activity: ActivitiesRow;
  leader: UsersRow;
}[] {
  const enrollmentsBySectionUid = _.groupBy(enrollments, "section_uid");
  const activitiesByUid = helpers.arrayToObject<ActivitiesRow>(
    activities,
    a => a.uid
  );
  const leadersByUid = helpers.arrayToObject<UsersRow>(leaders, l => l.uid);

  const listings = sections.map(section => {
    const enrollments = enrollmentsBySectionUid[section.uid];
    if (!enrollments || !enrollments.length) {
      return null;
    }
    const activity =
      !!section.activity_uid && activitiesByUid[section.activity_uid];
    if (!activity) {
      return null;
    }
    const leader =
      (!!activity.user_uid && leadersByUid[activity.user_uid]) ||
      (!!section.user_uid && leadersByUid[section.user_uid]);
    if (!leader) {
      return null;
    }
    return {
      enrollments,
      section,
      activity,
      leader
    };
  });
  return _.compact(listings);
}

type LearnerPartial = {
  name?: string | null;
  age?: string | number | null;
};
type DisplayEnrollmentPartial = {
  learner?: LearnerPartial | null;
  details?: LearnerPartial;
};

export const displayName = (
  enrollment: DisplayEnrollmentPartial,
  learner?: LearnerPartial | null
) => {
  learner = learner || enrollment.learner;
  return learner
    ? learner.name
    : _.get(enrollment, "details.name", "a gift enrollment");
};

export const unclaimedGiftDisplayName = (enrollment: $TSFixMe) => {
  return `${enrollment?.buyer?.name}'s unclaimed gift`;
};

export const displayAge = (
  enrollment: DisplayEnrollmentPartial,
  learner?: LearnerPartial | null
) => {
  learner = learner || enrollment.learner;
  return learner ? learner.age : _.get(enrollment, "details.age", "");
};

/**
 Get the formatted version of the order ID from an enrollment
 */
export const orderSlugId = (enrollment: $TSFixMe) => {
  // most of the time payments is length 1 and this only outputs Order xyz123,
  // but join with ', and' in case there a weird payment history for this enrollment

  const orderSlug = enrollment.payments.length
    ? joinCommasAnd(
        _.uniq(enrollment.payments.map((p: $TSFixMe) => p.order_slug_id))
      )
    : enrollment.orderSlugId;

  return `Order ${orderSlug}`;
};

export function studentNames(
  enrollments: Array<Pick<EnrollmentsRow, "learner_uid" | "details">>,
  learners: Array<{ uid: string; name: string | null | undefined }>
) {
  if (!enrollments?.length) {
    return undefined;
  }

  const names = enrollments
    .map(e =>
      displayName(
        e,
        learners.find(l => l.uid === e.learner_uid)
      )
    )
    .filter(Boolean)
    .sort();

  return joinCommasAnd(names);
}

/**
 * @deprecated Use Enrollment.studentNames
 */
export const studentNamesString = (
  enrollments: $TSFixMe,
  learnersByUid = {}
) => {
  if (!enrollments?.length) {
    return undefined;
  }

  const names = enrollments
    .map((e: $TSFixMe) =>
      // @ts-ignore
      displayName(e, e && (e.learner || learnersByUid[e.learner_uid]))
    )
    .filter(Boolean)
    .sort();
  return joinCommasAnd(names);
};

export const enrollmentTimeSummary = (
  enrollment: $TSFixMe,
  userTimeZone: $TSFixMe
) => {
  if (isOngoing(enrollment)) {
    const start = Time.formatDate(enrollment.ongoing_start_time, userTimeZone);
    if (enrollment.ongoing_end_time || enrollment.withdrawn_at) {
      const end = Time.formatDate(
        enrollment.ongoing_end_time || enrollment.withdrawn_at,
        userTimeZone
      );
      return `from ${start} to ${end}`;
    } else {
      return `since ${start}`;
    }
  } else {
    const enrollTime = Time.formatDate(enrollment.confirmed_at, userTimeZone);
    return `on ${enrollTime}`;
  }
};

export const enrollmentInputFromLearner = (
  learner: ChildInput,
  priceCents: number,
  pricingOffer?: { name: string }
): EnrollmentInput => {
  return {
    price_cents: priceCents,
    learner: {
      uid: learner.uid,
      name: learner.name ?? "",
      age: learner.age ?? 10,
      date_of_birth: learner.date_of_birth
    },
    pricing_offer: pricingOffer?.name
  };
};
export const bulkEnrollmentInputFromLearner = (
  activityUid: $TSFixMe,
  learner: ChildInput,
  priceCents: number,
  pricingOffer?: { name: string }
): BulkEnrollmentInput & { price_cents: number | null } => {
  return {
    activityUid,
    price_cents: priceCents,
    learner: {
      uid: learner.uid,
      name: learner.name ?? "",
      age: learner.age ?? 10
    },
    pricing_offer: pricingOffer?.name
  };
};
export const giftEnrollmentForSection = (
  priceCents: number,
  pricingOffer?: PricingOffersRow
): EnrollmentInput => ({
  price_cents: priceCents,
  pricing_offer: pricingOffer && pricingOffer.name,
  isGift: true
});

export const bulkGiftEnrollmentForSection = (
  // @ts-ignore
  activityUid,
  pricingOffer?: PricingOffersRow
): BulkEnrollmentInput => ({
  activityUid,
  pricing_offer: pricingOffer && pricingOffer.name,
  isGift: true
});

export const buyerUid = (enrollment: $TSFixMe) =>
  enrollment.purchased_by_user_uid || enrollment.user_uid;

export const sortEnrollmentsByLearner = (enrollments: $TSFixMe) => {
  return _.orderBy(
    enrollments,
    [
      e => (e.learner?.name || "zzzzz").toLowerCase(),
      e => (e.parent?.name || "zzzzz").toLowerCase(),
      e => e.parent?.uid || "zzzzz",
      "created_at"
    ],
    ["asc", "asc", "asc", "desc"]
  );
};

type GetEnrollmentEndTimeForTranscriptArgs = {
  ongoing_start_time?: Date;
  ongoingStartTime?: string;
  ongoingEndTime?: string;
  ongoing_end_time?: Date;
  section?: {
    start_time?: string;
    end_time?: string;
    meetings?: { end_time: string }[];
  } | null;
};
export const getEnrollmentEndTimeForTranscript = ({
  ongoing_start_time,
  ongoingStartTime,
  ongoingEndTime,
  ongoing_end_time,
  section
}: GetEnrollmentEndTimeForTranscriptArgs) => {
  if (ongoingStartTime || ongoing_start_time) {
    if (ongoingEndTime || ongoing_end_time) {
      // If the ongoing enrollment has ended
      return Time.dayjs(ongoingEndTime || ongoing_end_time);
    }

    //Corner case: If section is over but enrollment was not ended
    if (
      section?.end_time &&
      Time.dayjs(section.end_time).isBefore(Time.dayjs())
    ) {
      return Time.dayjs(section.end_time);
    }

    // If ongoing enrollment is still in progress now
    return Time.dayjs(
      Time.endOfIsoWeekUtc(Time.dayjs().subtract(1, "week").toDate())
    );
  }

  // Default sort for most enrollments
  if (section?.end_time) {
    return Time.dayjs(section.end_time);
  }

  // 1-1 enrollments have no end time, look at last meeting, then start time
  if (section?.meetings?.length) {
    return Time.dayjs(section?.meetings[0].end_time);
  }

  if (section?.start_time) {
    return Time.dayjs(section?.start_time);
  }

  // at this point, this will be invalid, but preserving existing behavior
  return Time.dayjs(section?.end_time);
};

export const endedBeforeSectionEnded = (
  enrollment: $TSFixMe,
  section: $TSFixMe
) => {
  return (
    enrollment.isEnded &&
    enrollment.ongoing_end_time &&
    section?.end_time &&
    Time.dayjs(enrollment.ongoing_end_time).isBefore(
      Time.dayjs(section?.end_time)
    )
  );
};

export const filterCompletedEnrollments = (enrollments: $TSFixMe) => {
  return enrollments.filter(
    (e: $TSFixMe) =>
      (isValid(e) &&
        e?.section?.end_time &&
        Time.dayjs(e.section.end_time).isBefore(Time.dayjs())) ||
      (isValid(e) &&
        hasOngoingStarted(e) &&
        Time.dayjs(Time.startOfIsoWeekUtc(new Date())).isAfter(
          Time.dayjs(e?.ongoing_start_time)
        )) ||
      (isWithdrawn(e) &&
        Time.dayjs(e?.withdrawn_at).add(1, "month").isAfter(Time.dayjs()))
  );
};

export type EnrollmentWithPayments = (
  | EnrollmentAdminRow_EnrollmentFragment
  | EnrollmentAdminRow_GiftEnrollmentFragment
) & { payments: Array<EnrollmentAdminRow_PaymentFragment> };

export const getActiveEnrollmentsForMeeting = (
  enrollments: Pick<
    EnrollmentsRow,
    "confirmed_at" | "withdrawn_at" | "ongoing_start_time" | "ongoing_end_time"
  >[],
  {
    meetingStartTime,
    meetingEndTime
  }: {
    meetingStartTime: Date;
    meetingEndTime: Date;
  }
) => {
  return enrollments.filter(
    e =>
      isValid(e) &&
      (!isOngoing(e) ||
        wasOngoingActiveAt(
          {
            ongoing_start_time:
              e.ongoing_start_time === null ? undefined : e.ongoing_start_time,
            ongoing_end_time:
              e.ongoing_end_time === null ? undefined : e.ongoing_end_time
          },
          meetingStartTime,
          meetingEndTime
        ))
  );
};

/**
 * Determine if an enrollment has a weekly payment schedule attached
 * Note: this does NOT include ongoing enrollments
 */
export function hasWeeklyPaymentSchedule({
  enrollment,
  activity
}: {
  enrollment: EnrollmentsRow;
  activity: ActivitiesRow;
}): boolean {
  if (enrollment.payment_schedule_uid && !activity.is_club) {
    return true;
  }
  return false;
}
